/* RESPONSIBLE TEAM: team-phone */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type Store from '@ember-data/store';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import { type FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import { request } from 'embercom/lib/inbox/requests';
import ConversationAttributeDescriptor, {
  type ConversationAttributeDescriptorWireFormat,
} from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { showNewMessageInIntercomWidget } from 'embercom/lib/intercom-widget-helper';
import IpAllowListValidator from 'embercom/lib/ip-allowlist-validator';

export type OfficeHoursScheduleOption = {
  value: string;
  text: string;
  description: string;
  isDefault: boolean;
};

export interface Schedule {
  id: string;
  name: string;
  labelWithTimezone: string;
  isNew: boolean;
  isDefault: boolean;
}

const PERMISSION_TO_CREATE_AND_EDIT_BOTS = 'can_create_and_edit_bots';

export default class FinVoiceConfigService extends Service {
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare permissionsService: $TSFixMe;

  @tracked numberConfigurations: Record<string, FinVoiceNumberConfiguration> = {};
  @tracked officeHoursSchedules: OfficeHoursScheduleOption[] = [];
  @tracked phoneNumbers: Record<string, PhoneNumber> = {};
  @tracked conversationAttributeDescriptors: ConversationAttributeDescriptor[] = [];
  @tracked numberConfigurationsLoaded = false;

  get defaultHandoverConfiguration(): FinVoiceNumberConfigurationHandoverConfiguration {
    return {
      in: {
        type: 'handover-to-voicemail',
        config: {},
      },
      nin: {
        type: 'handover-to-voicemail',
        config: {},
      },
    };
  }

  get isTrialExperience() {
    return this.appService.app.canUsePhoneFinVoiceFree;
  }

  get shouldShowPaywall(): boolean {
    return !this.appService.app.canSetupFinVoicePhoneEasySetup;
  }

  constructor(owner: object) {
    super(owner);
  }

  getDefaultOfficeHoursSchedule() {
    return this.officeHoursSchedules.find((schedule) => schedule.isDefault)?.value;
  }

  async checkPermissionToCreateAndEditBots() {
    await this.permissionsService.checkPermission(
      PERMISSION_TO_CREATE_AND_EDIT_BOTS,
      this.intl.t('operator.fin.setup.setup-and-go-live.not-authorized'),
    );
  }

  _formatNumberConfigurations(
    numberConfigurations: FinVoiceNumberConfiguration[],
    phoneNumbers: PhoneNumber[],
  ) {
    return phoneNumbers.reduce(
      (acc, phoneNumber) => {
        let numberConfiguration = numberConfigurations.find(
          (config) => config.workspacePhoneNumberId === phoneNumber.id,
        );

        if (numberConfiguration) {
          acc[phoneNumber.id] = numberConfiguration;
        } else if (phoneNumber.inboundEnabled) {
          acc[phoneNumber.id] = this.addNewNumberConfiguration(phoneNumber.id, false);
        }

        return acc;
      },
      {} as Record<string, FinVoiceNumberConfiguration>,
    );
  }

  async loadAllData() {
    if (this.numberConfigurationsLoaded) {
      return;
    }

    let [
      officeHoursSchedules,
      phoneNumbers,
      numberConfigurations,
      conversationAttributeDescriptors,
    ] = await Promise.all([
      this.fetchOfficeHoursSchedules(),
      this.fetchPhoneNumbers(),
      this.fetchConfigurations(),
      this.fetchConversationAttributeDescriptors(),
    ]);

    this.officeHoursSchedules = officeHoursSchedules;
    this.conversationAttributeDescriptors = conversationAttributeDescriptors;
    this.phoneNumbers = phoneNumbers.reduce(
      (acc, phoneNumber) => {
        acc[phoneNumber.id] = phoneNumber;
        return acc;
      },
      {} as Record<string, PhoneNumber>,
    );

    this.numberConfigurations = this._formatNumberConfigurations(
      numberConfigurations,
      phoneNumbers,
    );

    this.numberConfigurationsLoaded = true;
  }

  async fetchConfigurations() {
    return (await this.store.findAll('fin-voice/number-configuration')).toArray();
  }

  async fetchOfficeHoursSchedules() {
    let officeHoursSchedules: Schedule[] = (
      await this.store.findAll('office-hours-schedule')
    ).toArray();

    return officeHoursSchedules.map((schedule) => ({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    }));
  }

  async fetchPhoneNumbers() {
    let phoneNumbers = await this.store.findAll('calling-phone-number');

    return phoneNumbers.toArray().filter((phoneNumber) => phoneNumber.status === 'active');
  }

  async fetchConversationAttributeDescriptors() {
    let appid = this.appService.app.id;
    let response = await request(
      `/ember/conversation_attributes/descriptors?app_id=${appid}&system_defined=false`,
    );
    let json = (await response.json()) as ConversationAttributeDescriptorWireFormat[];
    return json
      .map(ConversationAttributeDescriptor.deserialize)
      .filter((attribute) => attribute.type === 'list' && !attribute.archived);
  }

  addNewNumberConfiguration(phoneNumberId: string, addToNumberConfigurations = true) {
    let phoneNumber = this.phoneNumbers[phoneNumberId];
    let newNumberConfiguration: FinVoiceNumberConfiguration = this.store.createRecord(
      'fin-voice/number-configuration',
      {
        appId: this.appService.app.id,
        workspacePhoneNumber: phoneNumber.phoneNumber,
        predicates: {},
        handoverConfiguration: this.defaultHandoverConfiguration,
        status: 'draft',
      },
    );

    if (addToNumberConfigurations) {
      this.numberConfigurations[phoneNumberId] = newNumberConfiguration;
    }

    return newNumberConfiguration;
  }

  private _validateIpAddress(ipAddress: string): boolean {
    let validator = new IpAllowListValidator(ipAddress, this.intl);
    return validator.isValid;
  }

  private async _updateIpRange(
    phoneNumberId: string,
    ipAddress: string,
    operation: 'add' | 'remove',
  ): Promise<boolean> {
    // Basic input validation
    if (!ipAddress) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.invalid-ip-address'),
      );
      return false;
    }

    if (!this.phoneNumbers[phoneNumberId]) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.phone-number-not-found'),
      );
      return false;
    }

    // Validate IP address format for add operation
    if (operation === 'add' && !this._validateIpAddress(ipAddress)) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.invalid-ip-address'),
      );
      return false;
    }

    try {
      let phoneNumber = this.phoneNumbers[phoneNumberId];
      let currentIpRange = [...phoneNumber.ipRangeArray]; // Create proper copy to avoid mutation

      if (operation === 'add') {
        // Check for duplicate IP address
        if (currentIpRange.includes(ipAddress)) {
          this.notificationsService.notifyError(
            this.intl.t('ai-agent.voice-playground.line-settings.ip-address-already-exists'),
          );
          return false;
        }
        currentIpRange.push(ipAddress);
      } else {
        // Remove operation
        currentIpRange = currentIpRange.filter((ip) => ip !== ipAddress);
      }

      phoneNumber.ipRange = currentIpRange.join(',');
      await phoneNumber.save();
      return true;
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.error-saving-ip-address'),
      );
      return false;
    }
  }

  async addIpAddress(phoneNumberId: string, ipAddress: string): Promise<boolean> {
    return this._updateIpRange(phoneNumberId, ipAddress, 'add');
  }

  async removeIpAddress(phoneNumberId: string, ipAddress: string): Promise<boolean> {
    return this._updateIpRange(phoneNumberId, ipAddress, 'remove');
  }

  async updatePercentageRollout(
    phoneNumberId: string,
    rollOutPercentageIn: number | undefined,
    rollOutPercentageNin: number | undefined,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.predicates = {
      officeHoursSchedule: numberConfiguration.predicates?.officeHoursSchedule,
      in: {
        percentageRollout: rollOutPercentageIn || 0,
      },
      nin: {
        percentageRollout: rollOutPercentageNin || 0,
      },
    };

    numberConfiguration.status = !rollOutPercentageIn && !rollOutPercentageNin ? 'draft' : 'active';

    await numberConfiguration.save();
  }

  async addAndSaveNewOfficeHoursSchedule(phoneNumberId: string, schedule: Schedule) {
    this.officeHoursSchedules.push({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    });

    await this.updateOfficeHoursSchedule(phoneNumberId, schedule.id);
  }

  async updateOfficeHoursSchedule(phoneNumberId: string, officeHoursSchedule: string) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.predicates.officeHoursSchedule = officeHoursSchedule;

    await numberConfiguration.save();
  }

  async updateVoiceBehavior(
    phoneNumberId: string,
    voice: FinVoiceVoice,
    greeting: string,
    greetingOutOfOfficeHours?: string,
    language?: FinVoiceVoiceLanguage,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.voice = voice;
    numberConfiguration.greeting = greeting;
    numberConfiguration.greetingOutOfOfficeHours = greetingOutOfOfficeHours;

    // Update the phone number's locale if language is provided
    if (language && this.phoneNumbers[phoneNumberId]) {
      this.phoneNumbers[phoneNumberId].locale = language;
      // Save the phone number to persist the locale change
      await this.phoneNumbers[phoneNumberId].save();
    }
    await numberConfiguration.save();
  }

  async updateAfterPhoneCallEnds(
    phoneNumberId: string,
    autoCloseWhenNotEscalated: boolean,
    defaultTeamId?: string,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    if (!numberConfiguration.handoverConfiguration) {
      numberConfiguration.handoverConfiguration = this.defaultHandoverConfiguration;
    }

    numberConfiguration.handoverConfiguration.autoCloseWhenNotEscalated = autoCloseWhenNotEscalated;
    numberConfiguration.handoverConfiguration.defaultTeamId = defaultTeamId;
    await numberConfiguration.save();
  }

  async updateFinIssueCategorization(phoneNumberId: string, attributes?: string[]) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.classificationAttributes = attributes;
    await numberConfiguration.save();
  }

  async updateHandoverConfiguration(
    phoneNumberId: string,
    handoverConfiguration: FinVoiceNumberConfigurationHandoverConfiguration,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.handoverConfiguration = {
      ...numberConfiguration.handoverConfiguration,
      ...handoverConfiguration,
    };
    await numberConfiguration.save();
  }

  talkToUs() {
    showNewMessageInIntercomWidget();
  }
}

declare module '@ember/service' {
  interface Registry {
    finVoiceConfigService: FinVoiceConfigService;
    'fin-voice-config-service': FinVoiceConfigService;
  }
}
